import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute - Requires authentication
 * Redirects to login if not authenticated
 */
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login with return path
    return <Navigate to="/erp/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * RoleBasedRoute - Requires specific role(s)
 * Redirects to appropriate dashboard if role doesn't match
 */
export const RoleBasedRoute = ({ children, allowedRoles = [] }) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/erp/login" state={{ from: location }} replace />;
  }

  // If no roles specified, allow all authenticated users
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check if user has required role
  const userRole = userProfile?.role;
  if (!userRole || !allowedRoles.includes(userRole)) {
    // Redirect to appropriate dashboard based on role
    let dashboardRoute = '/erp/dashboard-overview';
    
    switch (userRole) {
      case 'super_admin':
        dashboardRoute = '/erp/dashboard-overview';
        break;
      case 'hr_admin':
        dashboardRoute = '/erp/employee-management';
        break;
      case 'telecaller':
        dashboardRoute = '/erp/telecaller-dashboard';
        break;
      case 'staff':
        dashboardRoute = '/erp/client-requirements-matching';
        break;
      case 'client':
        dashboardRoute = '/erp/invoice-billing-management';
        break;
      case 'employee':
        dashboardRoute = '/erp/employee-self-service';
        break;
      default:
        dashboardRoute = '/erp/dashboard-overview';
    }

    return <Navigate to={dashboardRoute} replace />;
  }

  return children;
};

export default ProtectedRoute;

